import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import StudentDetail from '../components/admin/StudentDetail';
import {
  LayoutDashboard, BookOpen, Users, Clock, UserCheck, Settings,
  LogOut, Lock, Plus, Trash2, Edit3, Check, X, UserPlus,
  ToggleLeft, ToggleRight, Save, ChevronDown, Key, Calendar,
  Download, TrendingUp, FileText, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = async (path, token, options = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    method: options.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text);
  }
  return res.json();
};

const TABS = [
  { id: 'dashboard', icon: LayoutDashboard },
  { id: 'courses', icon: BookOpen },
  { id: 'students', icon: Users },
  { id: 'waiting', icon: Clock },
  { id: 'groups', icon: UserCheck },
  { id: 'settings', icon: Settings },
];

const TAB_LABELS = {
  sv: { dashboard: 'Översikt', courses: 'Kurser', students: 'Elever', waiting: 'Väntelista', groups: 'Grupper', settings: 'Inställningar' },
  en: { dashboard: 'Dashboard', courses: 'Courses', students: 'Students', waiting: 'Waiting List', groups: 'Groups', settings: 'Settings' },
};

const LEVEL_LABELS = {
  sv: { beginner: 'Nybörjare', intermediate: 'Medel', advanced: 'Avancerad' },
  en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
};

const STATUS_LABELS = {
  sv: { pending: 'Väntande', active: 'Aktiv', inactive: 'Inaktiv' },
  en: { pending: 'Pending', active: 'Active', inactive: 'Inactive' },
};

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  inactive: 'bg-red-500/20 text-red-400 border-red-500/30',
};

// ============ MODAL ============
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="rounded-lg p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
        style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          <button onClick={onClose} style={{ color: 'var(--text-muted)' }} className="hover:text-gold"><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
};

const InputField = ({ label, value, onChange, type = 'text', placeholder = '', required = false }) => (
  <div>
    <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      className="w-full px-3 py-2.5 rounded-lg text-sm"
    />
  </div>
);

// ============ DASHBOARD TAB ============
const DashboardTab = ({ token, language }) => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api('/admin/dashboard-stats', token).then(setStats).catch(() => {});
  }, [token]);

  if (!stats) return <div className="text-center py-12" style={{ color: 'var(--text-muted)' }}>Laddar...</div>;

  const cards = [
    { label: language === 'sv' ? 'Totalt elever' : 'Total Students', value: stats.total_students, color: 'text-blue-400' },
    { label: language === 'sv' ? 'Aktiva elever' : 'Active Students', value: stats.active_students, color: 'text-green-400' },
    { label: language === 'sv' ? 'Väntande ansökningar' : 'Pending Applications', value: stats.pending_students, color: 'text-yellow-400' },
    { label: language === 'sv' ? 'Väntelista' : 'Waiting List', value: stats.waiting_count, color: 'text-orange-400' },
    { label: language === 'sv' ? 'Grupper' : 'Groups', value: stats.groups_count, color: 'text-purple-400' },
    { label: language === 'sv' ? 'Kurser' : 'Courses', value: stats.courses_count, color: 'text-gold' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {cards.map((c, i) => (
          <div key={i} className="pricing-card !p-5" data-testid={`stat-card-${i}`}>
            <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>{c.label}</p>
            <p className={`text-3xl font-bold ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="pricing-card !p-5 flex items-center justify-between" data-testid="registration-status-card">
        <div>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {language === 'sv' ? 'Registreringsstatus' : 'Registration Status'}
          </p>
          <p className="text-lg font-semibold" style={{ color: stats.registration_open ? '#4ade80' : '#f87171' }}>
            {stats.registration_open
              ? (language === 'sv' ? 'Öppen' : 'Open')
              : (language === 'sv' ? 'Stängd' : 'Closed')}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {language === 'sv' ? 'Elevgräns' : 'Student Limit'}
          </p>
          <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
            {stats.active_students} / {stats.student_limit}
          </p>
        </div>
      </div>
    </div>
  );
};

// ============ COURSES TAB ============
const CoursesTab = ({ token, language }) => {
  const [courses, setCourses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', level: 'beginner', price_per_lesson: '', description: '', private_enabled: false, private_price_min: '', private_price_max: '' });

  const load = useCallback(() => {
    api('/admin/managed-courses', token).then(setCourses).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ name: '', level: 'beginner', price_per_lesson: '', description: '', private_enabled: false, private_price_min: '', private_price_max: '' }); setModal(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, level: c.level, price_per_lesson: String(c.price_per_lesson), description: c.description || '', private_enabled: c.private_enabled || false, private_price_min: String(c.private_price_min || ''), private_price_max: String(c.private_price_max || '') }); setModal(true); };

  const handleSave = async () => {
    const body = { ...form, price_per_lesson: parseFloat(form.price_per_lesson) || 0, private_price_min: parseFloat(form.private_price_min) || 0, private_price_max: parseFloat(form.private_price_max) || 0 };
    if (editing) {
      await api(`/admin/managed-courses/${editing.id}`, token, { method: 'PUT', body });
    } else {
      await api('/admin/managed-courses', token, { method: 'POST', body });
    }
    setModal(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort kursen?' : 'Delete this course?')) return;
    await api(`/admin/managed-courses/${id}`, token, { method: 'DELETE' });
    load();
  };

  return (
    <div data-testid="admin-courses">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {language === 'sv' ? 'Kurser' : 'Courses'} ({courses.length})
        </h3>
        <button onClick={openAdd} className="btn-gold !py-2 !px-4 flex items-center gap-2 text-sm" data-testid="add-course-btn">
          <Plus size={16} /> {language === 'sv' ? 'Lägg till' : 'Add'}
        </button>
      </div>
      <div className="space-y-3">
        {courses.map(c => (
          <div key={c.id} className="pricing-card !p-4 flex items-center justify-between" data-testid={`course-${c.id}`}>
            <div>
              <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{c.name}</p>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {LEVEL_LABELS[language]?.[c.level] || c.level} &middot; {c.price_per_lesson} SEK/{language === 'sv' ? 'lektion' : 'lesson'}
                {c.private_enabled && <span className="ml-2 text-gold">| {language === 'sv' ? 'Privat' : 'Private'}: {c.private_price_min}–{c.private_price_max} SEK</span>}
              </p>
              {c.description && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{c.description}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => openEdit(c)} className="p-2 rounded hover:bg-gold/10 transition-colors" style={{ color: 'var(--text-secondary)' }}><Edit3 size={16} /></button>
              <button onClick={() => handleDelete(c.id)} className="p-2 rounded hover:bg-red-500/10 text-red-400 transition-colors"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {courses.length === 0 && (
          <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
            {language === 'sv' ? 'Inga kurser ännu.' : 'No courses yet.'}
          </p>
        )}
      </div>
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? (language === 'sv' ? 'Redigera kurs' : 'Edit Course') : (language === 'sv' ? 'Ny kurs' : 'New Course')}>
        <div className="space-y-4">
          <InputField label={language === 'sv' ? 'Kursnamn' : 'Course Name'} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>{language === 'sv' ? 'Nivå' : 'Level'}</label>
            <select value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg text-sm">
              <option value="beginner">{LEVEL_LABELS[language]?.beginner}</option>
              <option value="intermediate">{LEVEL_LABELS[language]?.intermediate}</option>
              <option value="advanced">{LEVEL_LABELS[language]?.advanced}</option>
            </select>
          </div>
          <InputField label={language === 'sv' ? 'Pris per lektion (SEK)' : 'Price per lesson (SEK)'} value={form.price_per_lesson} onChange={v => setForm(f => ({ ...f, price_per_lesson: v }))} type="number" required />
          <InputField label={language === 'sv' ? 'Beskrivning' : 'Description'} value={form.description} onChange={v => setForm(f => ({ ...f, description: v }))} />
          {/* Private Lesson Pricing */}
          <div className="pt-2" style={{ borderTop: '1px solid var(--border-color)' }}>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{language === 'sv' ? 'Privatlektion (1-on-1)' : 'Private Lesson (1-on-1)'}</label>
              <button type="button" onClick={() => setForm(f => ({ ...f, private_enabled: !f.private_enabled }))}
                className="text-gold" data-testid="toggle-private-course">
                {form.private_enabled ? <ToggleRight size={30} /> : <ToggleLeft size={30} />}
              </button>
            </div>
            {form.private_enabled && (
              <div className="grid grid-cols-2 gap-3">
                <InputField label={language === 'sv' ? 'Min pris (SEK)' : 'Min Price (SEK)'} value={form.private_price_min} onChange={v => setForm(f => ({ ...f, private_price_min: v }))} type="number" required />
                <InputField label={language === 'sv' ? 'Max pris (SEK)' : 'Max Price (SEK)'} value={form.private_price_max} onChange={v => setForm(f => ({ ...f, private_price_max: v }))} type="number" required />
              </div>
            )}
          </div>
          <button onClick={handleSave} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="save-course-btn">
            <Save size={16} /> {language === 'sv' ? 'Spara' : 'Save'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ============ STUDENTS TAB ============
const StudentsTab = ({ token, language, onSelectStudent }) => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', age: '', email: '', phone: '', course: '', notes: '', level: '', teacher: '' });
  const [filter, setFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');

  const load = useCallback(() => {
    api('/admin/students', token).then(setStudents).catch(() => {});
    api('/admin/managed-courses', token).then(setCourses).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ name: '', age: '', email: '', phone: '', course: '', notes: '', level: '', teacher: '', book_password: '' }); setModal(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, age: s.age || '', email: s.email || '', phone: s.phone || '', course: s.course || '', notes: s.notes || '', level: s.level || '', teacher: s.teacher || '', book_password: '' }); setModal(true); };

  const handleSave = async () => {
    if (editing) {
      await api(`/admin/students/${editing.id}`, token, { method: 'PUT', body: form });
    } else {
      await api('/admin/students', token, { method: 'POST', body: form });
    }
    setModal(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort eleven?' : 'Delete this student?')) return;
    await api(`/admin/students/${id}`, token, { method: 'DELETE' });
    load();
  };

  const handleStatusChange = async (id, status) => {
    await api(`/admin/students/${id}`, token, { method: 'PUT', body: { status } });
    load();
  };

  const filtered = students.filter(s => {
    if (filter !== 'all' && s.status !== filter) return false;
    if (levelFilter !== 'all' && (s.level || '') !== levelFilter) return false;
    return true;
  });

  const PROGRESS_COLORS_INLINE = {
    beginner: 'bg-blue-500/20 text-blue-400',
    improving: 'bg-yellow-500/20 text-yellow-400',
    good: 'bg-green-500/20 text-green-400',
    excellent: 'bg-purple-500/20 text-purple-400',
  };

  return (
    <div data-testid="admin-students">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {language === 'sv' ? 'Elever' : 'Students'} ({students.length})
        </h3>
        <div className="flex gap-2 flex-wrap">
          {['all', 'active', 'pending', 'inactive'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${filter === f ? 'bg-gold text-black' : ''}`}
              style={filter !== f ? { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-color)' } : {}}>
              {f === 'all' ? (language === 'sv' ? 'Alla' : 'All') : STATUS_LABELS[language]?.[f]}
            </button>
          ))}
          <select value={levelFilter} onChange={e => setLevelFilter(e.target.value)}
            className="px-2 py-1 rounded text-xs" style={{ maxWidth: '120px' }} data-testid="level-filter">
            <option value="all">{language === 'sv' ? 'Alla nivåer' : 'All levels'}</option>
            <option value="beginner">{LEVEL_LABELS[language]?.beginner}</option>
            <option value="intermediate">{LEVEL_LABELS[language]?.intermediate}</option>
            <option value="advanced">{LEVEL_LABELS[language]?.advanced}</option>
            <option value="">{language === 'sv' ? 'Ej tilldelad' : 'Not assigned'}</option>
          </select>
          <button onClick={openAdd} className="btn-gold !py-1.5 !px-4 flex items-center gap-1 text-xs" data-testid="add-student-btn">
            <Plus size={14} /> {language === 'sv' ? 'Lägg till' : 'Add'}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {filtered.map(s => (
          <div key={s.id} className="pricing-card !p-4 flex items-center justify-between gap-4 cursor-pointer hover:border-gold/30 transition-colors" data-testid={`student-${s.id}`} onClick={() => onSelectStudent(s)}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{s.name}</p>
                <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[s.status] || ''}`}>
                  {STATUS_LABELS[language]?.[s.status] || s.status}
                </span>
                {s.level && <span className="px-2 py-0.5 rounded text-xs bg-gold/10 text-gold border border-gold/30">{LEVEL_LABELS[language]?.[s.level] || s.level}</span>}
                {s.progress_status && s.progress_status !== 'beginner' && (
                  <span className={`px-2 py-0.5 rounded text-xs ${PROGRESS_COLORS_INLINE[s.progress_status] || ''}`}>
                    {s.progress_status === 'improving' ? (language === 'sv' ? 'Förbättras' : 'Improving') :
                     s.progress_status === 'good' ? (language === 'sv' ? 'Bra' : 'Good') :
                     s.progress_status === 'excellent' ? (language === 'sv' ? 'Utmärkt' : 'Excellent') : ''}
                  </span>
                )}
              </div>
              <p className="text-xs mt-1 truncate" style={{ color: 'var(--text-muted)' }}>
                {[s.email, s.phone, s.course, s.teacher].filter(Boolean).join(' · ')}
              </p>
            </div>
            <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
              <select value={s.status} onChange={e => handleStatusChange(s.id, e.target.value)}
                className="px-2 py-1 rounded text-xs" style={{ maxWidth: '100px' }} data-testid={`student-status-${s.id}`}>
                <option value="pending">{STATUS_LABELS[language]?.pending}</option>
                <option value="active">{STATUS_LABELS[language]?.active}</option>
                <option value="inactive">{STATUS_LABELS[language]?.inactive}</option>
              </select>
              <button onClick={() => openEdit(s)} className="p-2 rounded hover:bg-gold/10 transition-colors" style={{ color: 'var(--text-secondary)' }}><Edit3 size={14} /></button>
              <button onClick={() => handleDelete(s.id)} className="p-2 rounded hover:bg-red-500/10 text-red-400 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'Inga elever.' : 'No students.'}</p>}
      </div>
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? (language === 'sv' ? 'Redigera elev' : 'Edit Student') : (language === 'sv' ? 'Ny elev' : 'New Student')}>
        <div className="space-y-4">
          <InputField label={language === 'sv' ? 'Namn' : 'Name'} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
          <div className="grid grid-cols-2 gap-3">
            <InputField label={language === 'sv' ? 'Ålder' : 'Age'} value={form.age} onChange={v => setForm(f => ({ ...f, age: v }))} />
            <InputField label={language === 'sv' ? 'Telefon' : 'Phone'} value={form.phone} onChange={v => setForm(f => ({ ...f, phone: v }))} />
          </div>
          <InputField label="E-post" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} type="email" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>{language === 'sv' ? 'Kurs' : 'Course'}</label>
              <select value={form.course} onChange={e => setForm(f => ({ ...f, course: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg text-sm">
                <option value="">{language === 'sv' ? 'Välj kurs...' : 'Select course...'}</option>
                {courses.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>{language === 'sv' ? 'Nivå' : 'Level'}</label>
              <select value={form.level} onChange={e => setForm(f => ({ ...f, level: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg text-sm">
                <option value="">{language === 'sv' ? 'Ej tilldelad' : 'Not assigned'}</option>
                <option value="beginner">{LEVEL_LABELS[language]?.beginner}</option>
                <option value="intermediate">{LEVEL_LABELS[language]?.intermediate}</option>
                <option value="advanced">{LEVEL_LABELS[language]?.advanced}</option>
              </select>
            </div>
          </div>
          <InputField label={language === 'sv' ? 'Lärare' : 'Teacher'} value={form.teacher} onChange={v => setForm(f => ({ ...f, teacher: v }))} />
          <InputField label={language === 'sv' ? 'Bokåtkomst (lösenord)' : 'Book Access (password)'} value={form.book_password || ''} onChange={v => setForm(f => ({ ...f, book_password: v }))} />
          <InputField label={language === 'sv' ? 'Anteckningar' : 'Notes'} value={form.notes} onChange={v => setForm(f => ({ ...f, notes: v }))} />
          <button onClick={handleSave} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="save-student-btn">
            <Save size={16} /> {language === 'sv' ? 'Spara' : 'Save'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

// ============ WAITING LIST TAB ============
const WaitingListTab = ({ token, language }) => {
  const [entries, setEntries] = useState([]);

  const load = useCallback(() => {
    api('/admin/waiting-list', token).then(setEntries).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const handleAccept = async (id) => {
    await api(`/admin/waiting-list/${id}/accept`, token, { method: 'POST' });
    load();
  };

  const handleRemove = async (id) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort från väntelistan?' : 'Remove from waiting list?')) return;
    await api(`/admin/waiting-list/${id}`, token, { method: 'DELETE' });
    load();
  };

  return (
    <div data-testid="admin-waiting-list">
      <h3 className="text-lg font-semibold mb-6" style={{ color: 'var(--text-primary)' }}>
        {language === 'sv' ? 'Väntelista' : 'Waiting List'} ({entries.length})
      </h3>
      <div className="space-y-3">
        {entries.map(e => (
          <div key={e.id} className="pricing-card !p-4" data-testid={`waiting-${e.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{e.name}</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  {[e.email, e.phone, e.preferred_course].filter(Boolean).join(' · ')}
                </p>
                {e.age && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'Ålder' : 'Age'}: {e.age}</p>}
                {e.message && <p className="text-xs mt-1 italic" style={{ color: 'var(--text-secondary)' }}>{e.message}</p>}
                {e.created_at && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(e.created_at).toLocaleDateString('sv-SE')}</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => handleAccept(e.id)}
                  className="px-3 py-1.5 rounded text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30 transition-colors"
                  data-testid={`accept-${e.id}`}
                >
                  <UserPlus size={14} className="inline mr-1" />
                  {language === 'sv' ? 'Acceptera' : 'Accept'}
                </button>
                <button
                  onClick={() => handleRemove(e.id)}
                  className="px-3 py-1.5 rounded text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30 transition-colors"
                >
                  <Trash2 size={14} className="inline mr-1" />
                  {language === 'sv' ? 'Ta bort' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        ))}
        {entries.length === 0 && <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'Väntelistan är tom.' : 'Waiting list is empty.'}</p>}
      </div>
    </div>
  );
};

// ============ GROUPS TAB ============
const GroupsTab = ({ token, language }) => {
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', course: '', schedule: '', max_students: '5' });
  const [lessonModal, setLessonModal] = useState(null);
  const [lessonForm, setLessonForm] = useState({
    lesson_date: new Date().toISOString().slice(0, 10), duration: 60, teacher: '',
    what_was_read: '', tajweed_corrections: '', memorization_progress: '', homework_assigned: '', notes: ''
  });
  const [studentAttendance, setStudentAttendance] = useState({});
  const [lessonSaving, setLessonSaving] = useState(false);
  const [lessonSaved, setLessonSaved] = useState(false);

  const load = useCallback(() => {
    api('/admin/groups', token).then(setGroups).catch(() => {});
    api('/admin/students', token).then(setStudents).catch(() => {});
    api('/admin/managed-courses', token).then(setCourses).catch(() => {});
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm({ name: '', course: '', schedule: '', max_students: '5' }); setModal(true); };
  const openEdit = (g) => { setEditing(g); setForm({ name: g.name, course: g.course || '', schedule: g.schedule || '', max_students: String(g.max_students || 5) }); setModal(true); };

  const openLessonLog = (g) => {
    setLessonModal(g);
    setLessonForm({ lesson_date: new Date().toISOString().slice(0, 10), duration: 60, teacher: '', what_was_read: '', tajweed_corrections: '', memorization_progress: '', homework_assigned: '', notes: '' });
    const att = {};
    (g.student_ids || []).forEach(sid => { att[sid] = 'present'; });
    setStudentAttendance(att);
    setLessonSaved(false);
  };

  const handleLogLesson = async () => {
    setLessonSaving(true);
    try {
      await api(`/admin/groups/${lessonModal.id}/lesson-log`, token, {
        method: 'POST',
        body: { ...lessonForm, student_attendance: studentAttendance }
      });
      setLessonSaved(true);
      setTimeout(() => { setLessonModal(null); setLessonSaved(false); }, 1200);
    } catch {}
    setLessonSaving(false);
  };

  const handleSave = async () => {
    const body = { ...form, max_students: parseInt(form.max_students) || 5 };
    if (editing) {
      await api(`/admin/groups/${editing.id}`, token, { method: 'PUT', body });
    } else {
      await api('/admin/groups', token, { method: 'POST', body });
    }
    setModal(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'sv' ? 'Ta bort gruppen?' : 'Delete this group?')) return;
    await api(`/admin/groups/${id}`, token, { method: 'DELETE' });
    load();
  };

  const addToGroup = async (groupId, studentId) => {
    await api(`/admin/groups/${groupId}/add-student`, token, { method: 'POST', body: { student_id: studentId } });
    load();
  };

  const removeFromGroup = async (groupId, studentId) => {
    await api(`/admin/groups/${groupId}/remove-student`, token, { method: 'POST', body: { student_id: studentId } });
    load();
  };

  const getStudentName = (id) => students.find(s => s.id === id)?.name || id;
  const getAvailableStudents = (group) => students.filter(s => s.status !== 'inactive' && !group.student_ids?.includes(s.id));
  const getDuration = (count) => count <= 2 ? '60 min' : '90 min';

  const ATT_LABELS = { present: language === 'sv' ? 'Närvarande' : 'Present', late: language === 'sv' ? 'Sen' : 'Late', absent: language === 'sv' ? 'Frånvarande' : 'Absent' };
  const ATT_COLORS = { present: 'bg-green-500/20 text-green-400', late: 'bg-yellow-500/20 text-yellow-400', absent: 'bg-red-500/20 text-red-400' };

  return (
    <div data-testid="admin-groups">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          {language === 'sv' ? 'Grupper' : 'Groups'} ({groups.length})
        </h3>
        <button onClick={openAdd} className="btn-gold !py-2 !px-4 flex items-center gap-2 text-sm" data-testid="add-group-btn">
          <Plus size={16} /> {language === 'sv' ? 'Skapa grupp' : 'Create Group'}
        </button>
      </div>
      <div className="space-y-4">
        {groups.map(g => {
          const memberCount = g.student_ids?.length || 0;
          return (
            <div key={g.id} className="pricing-card !p-5" data-testid={`group-${g.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>{g.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    {g.course && `${g.course} · `}{g.schedule && `${g.schedule} · `}{getDuration(memberCount)} · {memberCount}/{g.max_students || 5} {language === 'sv' ? 'elever' : 'students'}
                  </p>
                </div>
                <div className="flex gap-2">
                  {memberCount > 0 && (
                    <button onClick={() => openLessonLog(g)} className="px-3 py-1.5 rounded text-xs font-medium bg-gold/20 text-gold border border-gold/30 hover:bg-gold/30 transition-colors" data-testid={`log-lesson-${g.id}`}>
                      <FileText size={14} className="inline mr-1" />
                      {language === 'sv' ? 'Logga lektion' : 'Log Lesson'}
                    </button>
                  )}
                  <button onClick={() => openEdit(g)} className="p-2 rounded hover:bg-gold/10 transition-colors" style={{ color: 'var(--text-secondary)' }}><Edit3 size={16} /></button>
                  <button onClick={() => handleDelete(g.id)} className="p-2 rounded hover:bg-red-500/10 text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="space-y-1.5 mb-3">
                {(g.student_ids || []).map(sid => (
                  <div key={sid} className="flex items-center justify-between py-1.5 px-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{getStudentName(sid)}</span>
                    <button onClick={() => removeFromGroup(g.id, sid)} className="text-red-400 hover:text-red-300"><X size={14} /></button>
                  </div>
                ))}
              </div>
              {memberCount < (g.max_students || 5) && (
                <AddStudentDropdown
                  availableStudents={getAvailableStudents(g)}
                  onAdd={(sid) => addToGroup(g.id, sid)}
                  language={language}
                />
              )}
            </div>
          );
        })}
        {groups.length === 0 && <p className="text-center py-8" style={{ color: 'var(--text-muted)' }}>{language === 'sv' ? 'Inga grupper ännu.' : 'No groups yet.'}</p>}
      </div>

      {/* Group Edit/Create Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editing ? (language === 'sv' ? 'Redigera grupp' : 'Edit Group') : (language === 'sv' ? 'Ny grupp' : 'New Group')}>
        <div className="space-y-4">
          <InputField label={language === 'sv' ? 'Gruppnamn' : 'Group Name'} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>{language === 'sv' ? 'Kurs' : 'Course'}</label>
            <select value={form.course} onChange={e => setForm(f => ({ ...f, course: e.target.value }))} className="w-full px-3 py-2.5 rounded-lg text-sm">
              <option value="">{language === 'sv' ? 'Välj kurs...' : 'Select course...'}</option>
              {courses.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <InputField label={language === 'sv' ? 'Schema' : 'Schedule'} value={form.schedule} onChange={v => setForm(f => ({ ...f, schedule: v }))} placeholder={language === 'sv' ? 'T.ex. Mån & Ons 17:00' : 'E.g. Mon & Wed 17:00'} />
          <InputField label={language === 'sv' ? 'Max elever' : 'Max Students'} value={form.max_students} onChange={v => setForm(f => ({ ...f, max_students: v }))} type="number" />
          <button onClick={handleSave} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="save-group-btn">
            <Save size={16} /> {language === 'sv' ? 'Spara' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* Group Lesson Log Modal */}
      <Modal isOpen={!!lessonModal} onClose={() => setLessonModal(null)} title={language === 'sv' ? `Logga lektion — ${lessonModal?.name || ''}` : `Log Lesson — ${lessonModal?.name || ''}`}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <InputField label={language === 'sv' ? 'Datum' : 'Date'} value={lessonForm.lesson_date} onChange={v => setLessonForm(f => ({ ...f, lesson_date: v }))} type="date" />
            <InputField label={language === 'sv' ? 'Längd (min)' : 'Duration (min)'} value={String(lessonForm.duration)} onChange={v => setLessonForm(f => ({ ...f, duration: parseInt(v) || 60 }))} type="number" />
          </div>
          <InputField label={language === 'sv' ? 'Lärare' : 'Teacher'} value={lessonForm.teacher} onChange={v => setLessonForm(f => ({ ...f, teacher: v }))} />
          <InputField label={language === 'sv' ? 'Vad lästes' : 'What was read'} value={lessonForm.what_was_read} onChange={v => setLessonForm(f => ({ ...f, what_was_read: v }))} />
          <InputField label={language === 'sv' ? 'Tajweed-korrigeringar' : 'Tajweed corrections'} value={lessonForm.tajweed_corrections} onChange={v => setLessonForm(f => ({ ...f, tajweed_corrections: v }))} />
          <InputField label={language === 'sv' ? 'Läxa' : 'Homework'} value={lessonForm.homework_assigned} onChange={v => setLessonForm(f => ({ ...f, homework_assigned: v }))} />
          <InputField label={language === 'sv' ? 'Anteckningar' : 'Notes'} value={lessonForm.notes} onChange={v => setLessonForm(f => ({ ...f, notes: v }))} />

          {/* Per-student attendance */}
          <div className="pt-2" style={{ borderTop: '1px solid var(--border-color)' }}>
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--text-primary)' }}>
              {language === 'sv' ? 'Närvaro' : 'Attendance'}
            </p>
            <div className="space-y-2">
              {(lessonModal?.student_ids || []).map(sid => (
                <div key={sid} className="flex items-center justify-between py-2 px-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{getStudentName(sid)}</span>
                  <div className="flex gap-1">
                    {['present', 'late', 'absent'].map(att => (
                      <button key={att} onClick={() => setStudentAttendance(prev => ({ ...prev, [sid]: att }))}
                        className={`px-2 py-1 rounded text-xs font-medium transition-all ${studentAttendance[sid] === att ? ATT_COLORS[att] : ''}`}
                        style={studentAttendance[sid] !== att ? { color: 'var(--text-muted)', border: '1px solid var(--border-color)' } : {}}
                        data-testid={`att-${sid}-${att}`}>
                        {ATT_LABELS[att]}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={handleLogLesson} disabled={lessonSaving} className="btn-gold w-full flex items-center justify-center gap-2" data-testid="save-group-lesson-btn">
            {lessonSaved ? <Check size={16} /> : <Save size={16} />}
            {lessonSaved ? (language === 'sv' ? 'Sparat!' : 'Saved!') : (language === 'sv' ? 'Spara lektion' : 'Save Lesson')}
          </button>
        </div>
      </Modal>
    </div>
  );
};

const AddStudentDropdown = ({ availableStudents, onAdd, language }) => {
  const [open, setOpen] = useState(false);
  if (availableStudents.length === 0) return null;
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light transition-colors" data-testid="add-student-to-group-btn">
        <UserPlus size={14} /> {language === 'sv' ? 'Lägg till elev' : 'Add student'} <ChevronDown size={12} />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-60 rounded-lg py-1 max-h-48 overflow-y-auto" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
          {availableStudents.map(s => (
            <button key={s.id} onClick={() => { onAdd(s.id); setOpen(false); }} className="w-full text-left px-3 py-2 text-sm hover:bg-gold/10 transition-colors" style={{ color: 'var(--text-secondary)' }}>
              {s.name} {s.course && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>({s.course})</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ SETTINGS TAB ============
const SettingsTab = ({ token, language }) => {
  const [systemSettings, setSystemSettings] = useState({ student_limit: 30, registration_open: true });
  const [siteSettings, setSiteSettings] = useState({ student_code: '', calendly_url: '', book_access_code: '' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');

  useEffect(() => {
    api('/admin/system-settings', token).then(setSystemSettings).catch(() => {});
    api('/admin/settings', token).then(setSiteSettings).catch(() => {});
  }, [token]);

  const saveSystem = async () => {
    setSaving(true);
    await api('/admin/system-settings', token, { method: 'PUT', body: systemSettings });
    setSaved('system');
    setTimeout(() => setSaved(''), 2000);
    setSaving(false);
  };

  const saveSite = async () => {
    setSaving(true);
    await api('/admin/settings', token, { method: 'PUT', body: siteSettings });
    setSaved('site');
    setTimeout(() => setSaved(''), 2000);
    setSaving(false);
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-tab">
      {/* System Settings */}
      <div className="pricing-card !p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Settings size={20} className="text-gold" />
          {language === 'sv' ? 'Systeminställningar' : 'System Settings'}
        </h3>
        <div className="space-y-5">
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              {language === 'sv' ? 'Max antal elever' : 'Student Limit'}
            </label>
            <input
              type="number"
              value={systemSettings.student_limit}
              onChange={e => setSystemSettings(s => ({ ...s, student_limit: parseInt(e.target.value) || 0 }))}
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              data-testid="student-limit-input"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {language === 'sv' ? 'Registrering' : 'Registration'}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {systemSettings.registration_open
                  ? (language === 'sv' ? 'Öppen - nya elever kan ansöka' : 'Open - new students can apply')
                  : (language === 'sv' ? 'Stängd - nya ansökningar går till väntelistan' : 'Closed - new applications go to waiting list')}
              </p>
            </div>
            <button
              onClick={() => setSystemSettings(s => ({ ...s, registration_open: !s.registration_open }))}
              className="text-gold"
              data-testid="toggle-registration"
            >
              {systemSettings.registration_open ? <ToggleRight size={36} /> : <ToggleLeft size={36} />}
            </button>
          </div>
          <button onClick={saveSystem} className="btn-gold w-full flex items-center justify-center gap-2" disabled={saving} data-testid="save-system-settings-btn">
            {saved === 'system' ? <Check size={16} /> : <Save size={16} />}
            {saved === 'system' ? (language === 'sv' ? 'Sparat!' : 'Saved!') : (language === 'sv' ? 'Spara systeminställningar' : 'Save System Settings')}
          </button>
        </div>
      </div>

      {/* Site Settings */}
      <div className="pricing-card !p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Key size={20} className="text-gold" />
          {language === 'sv' ? 'Webbplatsinställningar' : 'Site Settings'}
        </h3>
        <div className="space-y-5">
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              {language === 'sv' ? 'Elevkod (för material)' : 'Student Code (for materials)'}
            </label>
            <input
              type="text"
              value={siteSettings.student_code}
              onChange={e => setSiteSettings(s => ({ ...s, student_code: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              data-testid="student-code-input"
            />
          </div>
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              {language === 'sv' ? 'Bokkod (Tajweed & Quran Online Method)' : 'Book Access Code (Tajweed & Quran Online Method)'}
            </label>
            <input
              type="text"
              value={siteSettings.book_access_code}
              onChange={e => setSiteSettings(s => ({ ...s, book_access_code: e.target.value }))}
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              placeholder="quran2026"
              data-testid="book-access-code-input"
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              {language === 'sv' ? 'Denna kod delar du med elever för att ge tillgång till böckerna' : 'Share this code with students to give book access'}
            </p>
          </div>
          <div>
            <label className="block text-sm mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              {language === 'sv' ? 'Calendly-länk' : 'Calendly Link'}
            </label>
            <input
              type="url"
              value={siteSettings.calendly_url}
              onChange={e => setSiteSettings(s => ({ ...s, calendly_url: e.target.value }))}
              placeholder="https://calendly.com/..."
              className="w-full px-3 py-2.5 rounded-lg text-sm"
              data-testid="calendly-url-input"
            />
          </div>
          <button onClick={saveSite} className="btn-gold w-full flex items-center justify-center gap-2" disabled={saving} data-testid="save-site-settings-btn">
            {saved === 'site' ? <Check size={16} /> : <Save size={16} />}
            {saved === 'site' ? (language === 'sv' ? 'Sparat!' : 'Saved!') : (language === 'sv' ? 'Spara webbplatsinställningar' : 'Save Site Settings')}
          </button>
        </div>
      </div>

      {/* CSV Export */}
      <div className="pricing-card !p-6">
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <Download size={20} className="text-gold" />
          {language === 'sv' ? 'Exportera data' : 'Export Data'}
        </h3>
        <div className="flex gap-3">
          <a href={`${API_URL}/api/admin/export/students`} className="btn-gold !py-2 !px-4 flex items-center gap-2 text-sm no-underline"
            onClick={(e) => { e.preventDefault(); fetch(`${API_URL}/api/admin/export/students`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(b => { const url = URL.createObjectURL(b); const a = document.createElement('a'); a.href = url; a.download = 'students.csv'; a.click(); }); }}
            data-testid="export-students-csv">
            <Download size={14} /> {language === 'sv' ? 'Elever (CSV)' : 'Students (CSV)'}
          </a>
          <a href={`${API_URL}/api/admin/export/waiting-list`} className="btn-gold !py-2 !px-4 flex items-center gap-2 text-sm no-underline"
            onClick={(e) => { e.preventDefault(); fetch(`${API_URL}/api/admin/export/waiting-list`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.blob()).then(b => { const url = URL.createObjectURL(b); const a = document.createElement('a'); a.href = url; a.download = 'waiting_list.csv'; a.click(); }); }}
            data-testid="export-waiting-csv">
            <Download size={14} /> {language === 'sv' ? 'Väntelista (CSV)' : 'Waiting List (CSV)'}
          </a>
        </div>
      </div>
    </div>
  );
};

// ============ MAIN ADMIN PAGE ============
const AdminPage = () => {
  const { language } = useLanguage();
  const [token, setToken] = useState(() => sessionStorage.getItem('quran-admin-token') || '');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedStudent, setSelectedStudent] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        const data = await res.json();
        setToken(data.token);
        sessionStorage.setItem('quran-admin-token', data.token);
      } else if (res.status === 401) {
        setLoginError(language === 'sv' ? 'Fel lösenord. Försök igen.' : 'Wrong password. Try again.');
      } else {
        setLoginError(language === 'sv' ? 'Serverfel. Försök igen om en stund.' : 'Server error. Try again later.');
      }
    } catch {
      setLoginError(language === 'sv' ? 'Kunde inte ansluta till servern. Kontrollera din internetanslutning och försök igen.' : 'Could not connect to server. Check your internet and try again.');
    }
    setLoading(false);
  };

  const handleLogout = () => {
    setToken('');
    sessionStorage.removeItem('quran-admin-token');
    setPassword('');
  };

  if (!token) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="admin-page">
        <div className="pricing-card text-center max-w-md w-full mx-4" data-testid="admin-login">
          <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center bg-gold/10 border border-gold/20">
            <Lock size={28} className="text-gold" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            {language === 'sv' ? 'Admin-inloggning' : 'Admin Login'}
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--text-secondary)' }}>
            {language === 'sv' ? 'Ange ditt adminlösenord' : 'Enter your admin password'}
          </p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setLoginError(''); }}
              placeholder={language === 'sv' ? 'Adminlösenord...' : 'Admin password...'}
              className="w-full px-4 py-3 rounded-lg mb-4 text-sm"
              data-testid="admin-password-input"
            />
            {loginError && <p className="text-red-400 text-sm mb-4" data-testid="admin-login-error">{loginError}</p>}
            <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="admin-login-btn">
              {loading ? '...' : language === 'sv' ? 'Logga in' : 'Log In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const renderTab = () => {
    if (selectedStudent && activeTab === 'students') {
      return <StudentDetail student={selectedStudent} token={token} language={language} onBack={() => setSelectedStudent(null)} onRefresh={() => setSelectedStudent(null)} />;
    }
    switch (activeTab) {
      case 'dashboard': return <DashboardTab token={token} language={language} />;
      case 'courses': return <CoursesTab token={token} language={language} />;
      case 'students': return <StudentsTab token={token} language={language} onSelectStudent={(s) => setSelectedStudent(s)} />;
      case 'waiting': return <WaitingListTab token={token} language={language} />;
      case 'groups': return <GroupsTab token={token} language={language} />;
      case 'settings': return <SettingsTab token={token} language={language} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="admin-page">
      {/* Header */}
      <div className="py-6" style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {language === 'sv' ? 'Adminpanel' : 'Admin Panel'}
          </h1>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm hover:text-gold transition-colors" style={{ color: 'var(--text-muted)' }} data-testid="admin-logout">
            <LogOut size={16} /> {language === 'sv' ? 'Logga ut' : 'Log Out'}
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ backgroundColor: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-2" data-testid="admin-tabs">
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSelectedStudent(null); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    isActive ? 'bg-gold text-black' : 'hover:bg-gold/10'
                  }`}
                  style={!isActive ? { color: 'var(--text-secondary)' } : {}}
                  data-testid={`tab-${tab.id}`}
                >
                  <Icon size={16} />
                  {TAB_LABELS[language]?.[tab.id] || tab.id}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {renderTab()}
      </div>
    </div>
  );
};

export default AdminPage;
